import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output } from "@angular/core";
import { getComegoFutureLockedDate, getComegoLockedDate } from "@app/_helpers/timeLock";
import { createRxValue } from "@app/_helpers/utils";
import { TranslateService } from "@ngx-translate/core";
import { UserSettingsQuery } from "@timeghost/web-api";
import { startOfDay } from "date-fns";
import {
	addDays,
	addMonths,
	addQuarters,
	addWeeks,
	addYears,
	endOfDay,
	endOfWeek,
	isSameDay,
	isSameYear,
	startOfWeek,
	subDays,
	subMonths,
	subQuarters,
	subWeeks,
	subYears,
} from "date-fns/esm";
import { Subject } from "rxjs";
import { distinctUntilChanged, filter, map, takeUntil } from "rxjs/operators";
import { CalendarRangePickerService } from "./calendar-range-picker/calendar-range-picker.service";
import { shiftCustomTimeRange } from "./shift-time-range";
import {
	applyTimeRangeAfterParse,
	getTimeRangeByType,
	TIME_RANGE_CUSTOM_NAME,
	TIME_RANGE_DEFAULT_RANGES,
	TRange,
	TRangePresets,
} from "./time-range-constants";

@Component({
	selector: "app-time-range-picker",
	templateUrl: "./time-range-picker.component.html",
	styleUrls: ["./time-range-picker.component.scss"],
})
export class TimeRangePickerComponent implements OnDestroy {
	@Input()
	fixedMidButtonWidth: number = null;
	private readonly destroy$ = new Subject<void>();
	readonly defaultRanges = createRxValue<TRangePresets>(TIME_RANGE_DEFAULT_RANGES);
	@Output()
	readonly rangeChange = new EventEmitter<TRange>(true);
	private readonly _range = createRxValue<TRange>(TIME_RANGE_DEFAULT_RANGES.week);
	private hasSameRange(left: TRange, right: TRange) {
		if (!left || !right) return false;
		return (
			new Date(left.from).getTime() === new Date(right.from).getTime() &&
			new Date(left.to).getTime() === new Date(right.to).getTime() &&
			(left.name || TIME_RANGE_CUSTOM_NAME) === (right.name || TIME_RANGE_CUSTOM_NAME) &&
			(left.rangeType || null) === (right.rangeType || null)
		);
	}
	private setRangeState(nextRange: TRange, emit: boolean) {
		const currentRange = this._range.value;
		if (this.hasSameRange(currentRange, nextRange)) return;
		this._range.next(nextRange);
		if (emit) this.rangeChange.emit(nextRange);
	}
	@Input()
	set range(val: TRange) {
		if (!val) return;
		this.setRangeState(val, false);
	}
	private readonly _employee = createRxValue<string[]>([]);
	@Input()
	set employee(val: string[]) {
		this._employee.next(val || []);
	}
	@Input()
	showPresets: boolean = true;
	@Input()
	disabled: boolean = false;
	@Input()
	allowDynamic: boolean = true;
	@Input()
	allowGeneralize: boolean = true;
	readonly range$ = this._range
		.asObservable()
		.pipe(distinctUntilChanged((left, right) => this.hasSameRange(left, right)));
	readonly range$SameDay = this.range$.pipe(map((x) => isSameDay(x.to, x.from)));
	readonly range$SameYear = this.range$.pipe(
		map((x) => {
			const now = new Date();
			return isSameYear(x.from, now) && isSameYear(x.to, now);
		})
	);
	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}
	constructor(
		private translateService: TranslateService,
		private cldrDialog: CalendarRangePickerService,
		private userSettingsQuery: UserSettingsQuery
	) {}
	get currentLocale() {
		return this.translateService.currentLang;
	}
	allowedDates() {
		const currentUser = this.userSettingsQuery.getValue();
		const selectedUser = this._employee.value.find((x) => x === currentUser.id) || "";
		const pastDate = getComegoLockedDate(currentUser, selectedUser);
		const futureDate = getComegoFutureLockedDate(currentUser, selectedUser);
		return [pastDate, futureDate];
	}
	openRangePicker(ref: ElementRef<any>) {
		const range = this._range.value;
		const dates = this.allowedDates();
		this.cldrDialog
			.open({
				config: {
					elRef: ref,
					presets: TIME_RANGE_DEFAULT_RANGES,
					minDate: dates[0],
					maxDate: dates[1],
					selected:
						(range && {
							...range,
							rangeType: range.name === TIME_RANGE_CUSTOM_NAME ? null : range.rangeType,
						}) ||
						null,
					allowDynamic: this.allowDynamic,
					allowGeneralize: this.allowGeneralize,
				},
			})
			.afterClosed()
			.pipe(
				filter((x: TRange) => !!x),
				takeUntil(this.destroy$)
			)
			.subscribe((x) => this.setRangeState(this.rangeParserFixer(applyTimeRangeAfterParse({ ...x })), true));
	}
	selectRange(r: TRange) {
		const isCustom = TIME_RANGE_CUSTOM_NAME === r.name;
		const rangeType = r.rangeType;
		let range = {
			...r,
			rangeType,
			...((rangeType && getTimeRangeByType(rangeType)) || {
				name: "time-range.preset.custom",
				rangeType: null,
			}),
			from: startOfDay(r.from),
			to: endOfDay(r.to),
			custom: isCustom ? (isSameDay(r.from, r.to) ? null : r.custom) : r.custom,
		};
		this.setRangeState(this.rangeParserFixer(range), true);
	}
	private rangeParserFixer(range: TRange) {
		if (this.rangeParser === "week") {
			range.from = startOfWeek(range.from, { weekStartsOn: 1 });
			range.to = endOfWeek(range.to, { weekStartsOn: 1 });
		}
		return range;
	}
	@Input()
	rangeParser: "week" | null = null;
	private shiftRange(r: TRange, direction: -1 | 1): TRange {
		const shiftFn = {
			year: direction < 0 ? subYears : addYears,
			quarter: direction < 0 ? subQuarters : addQuarters,
			month: direction < 0 ? subMonths : addMonths,
			week: direction < 0 ? subWeeks : addWeeks,
			day: direction < 0 ? subDays : addDays,
		};
		const customShift = direction < 0 ? r.custom?.sub : r.custom?.add;
		let from = new Date(r.from.getTime());
		let to = new Date(r.to.getTime());
		if (customShift) {
			from = customShift(from);
			to = customShift(to);
		} else if (r.rangeType && shiftFn[r.rangeType as keyof typeof shiftFn]) {
			from = shiftFn[r.rangeType as keyof typeof shiftFn](from, 1);
			to = shiftFn[r.rangeType as keyof typeof shiftFn](to, 1);
		} else {
			// custom / unknown: full calendar unit when aligned, else inclusive day length
			({ from, to } = shiftCustomTimeRange(from, to, direction));
		}
		return {
			...(r.afterParse?.({ ...r, from, to, name: TIME_RANGE_CUSTOM_NAME }) ?? {
				...r,
				from,
				to,
				name: TIME_RANGE_CUSTOM_NAME,
			}),
		};
	}
	goBack(r: TRange) {
		this.setRangeState(this.rangeParserFixer(this.shiftRange(r, -1)), true);
	}
	goForward(r: TRange) {
		this.setRangeState(this.rangeParserFixer(this.shiftRange(r, 1)), true);
	}
}
